import React from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, MapPin, Mail, MessageSquare } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { Logo } from "./Logo";

export const Footer = () => {
  const { settings, tr, lang } = useApp();
  const waLink = `https://wa.me/${settings.whatsapp_sales}?text=${encodeURIComponent(tr.whatsappMsgs.general)}`;

  return (
    <footer className="bg-[#0B132B] text-white mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-1">
            <div className="bg-white rounded-2xl p-3 inline-block">
              <Logo className="h-12" />
            </div>
            <p className="mt-4 text-sm text-slate-400 max-w-xs">
              {lang === "ar"
                ? "التوزيع العالمي للهواتف المحمولة. موزع Blackview المعتمد."
                : "Global Mobile Distribution from Dubai. Authorized Distributor of Blackview."}
            </p>
            <p className="mt-4 text-xs text-slate-300 font-semibold">{settings.legal_name || "ALFUKIKI TRADING FZCO"}</p>
            {lang === "ar" && <p className="text-xs text-slate-300" dir="rtl">{settings.legal_name_ar || "الفكيكي للتجارة ش.م.ح"}</p>}
            <p className="mt-2 text-xs text-slate-500">5WA 206, Second Floor, 5 West A,<br />Dubai Airport Freezone, Dubai, UAE</p>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-400 mb-4">{tr.footer.quickLinks}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="hover:text-[#3B82F6] transition-colors" data-testid="footer-nav-home">{tr.nav.home}</Link></li>
              <li><Link to="/phones" className="hover:text-[#3B82F6] transition-colors" data-testid="footer-nav-phones">{tr.nav.phones}</Link></li>
              <li><Link to="/phones?brand=Blackview" className="hover:text-[#3B82F6] transition-colors font-semibold text-[#4785FF]" data-testid="footer-nav-blackview">Blackview</Link></li>
              <li><Link to="/about" className="hover:text-[#3B82F6] transition-colors" data-testid="footer-nav-about">{tr.nav.about}</Link></li>
              <li><Link to="/contact" className="hover:text-[#3B82F6] transition-colors" data-testid="footer-nav-contact">{tr.nav.contact}</Link></li>
              <li><Link to="/request-quote" className="hover:text-[#3B82F6] transition-colors" data-testid="footer-nav-quote">{tr.nav.quote}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-400 mb-4">{tr.footer.contact}</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <p className="text-slate-400 text-xs uppercase tracking-[0.15em]">{lang === "ar" ? "الإدارة" : "Management"} · {settings.management_name}</p>
                <a href={`tel:${(settings.management_phone || "").replace(/\s/g, "")}`} className="flex items-center gap-2 hover:text-[#3B82F6]" data-testid="footer-mgmt-phone">
                  <Phone className="w-4 h-4" /> {settings.management_phone}
                </a>
              </li>
              {settings.management2_name && (
                <li>
                  <p className="text-slate-400 text-xs uppercase tracking-[0.15em]">{lang === "ar" ? "الإدارة" : "Management"} · {settings.management2_name}</p>
                  <a href={`tel:${(settings.management2_phone || "").replace(/\s/g, "")}`} className="flex items-center gap-2 hover:text-[#3B82F6]" data-testid="footer-mgmt2-phone">
                    <Phone className="w-4 h-4" /> {settings.management2_phone}
                  </a>
                </li>
              )}
              <li>
                <p className="text-slate-400 text-xs uppercase tracking-[0.15em]">{lang === "ar" ? "مبيعات ومشتريات" : "Sales & Purchasing"} · {settings.sales_name}</p>
                <a href={`tel:${(settings.sales_phone || "").replace(/\s/g, "")}`} className="flex items-center gap-2 hover:text-[#3B82F6]" data-testid="footer-sales-phone">
                  <Phone className="w-4 h-4" /> {settings.sales_phone}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-400 mb-4">{lang === "ar" ? "تواصل معنا" : "Reach Out"}</h4>
            <ul className="space-y-2 text-sm">
              <li><a href={`mailto:${settings.email_sales}`} className="flex items-center gap-2 hover:text-[#3B82F6]" data-testid="footer-email-sales"><Mail className="w-4 h-4" /> {settings.email_sales}</a></li>
              <li><span className="flex items-center gap-2 text-slate-400"><MessageSquare className="w-4 h-4" /> WeChat · {settings.wechat_management || "Hayder-Alsaadi1"}</span></li>
              <li><span className="flex items-center gap-2 text-slate-400"><MessageSquare className="w-4 h-4" /> WeChat · {settings.wechat_sales || "Taha-alzaki-92"}</span></li>
            </ul>
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1EBE57] text-white rounded-full px-5 py-2.5 text-sm font-medium transition-colors"
              data-testid="footer-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp Sales · {settings.management_phone}
            </a>
          </div>
        </div>

        <div className="blue-line my-10" />

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <div>© {new Date().getFullYear()} {settings.legal_name || "ALFUKIKI TRADING FZCO"} · AFC TELECOM. {tr.footer.rights}</div>
          <div className="font-display tracking-[0.3em] text-slate-600">AFC · TELECOM · DUBAI</div>
        </div>
      </div>
    </footer>
  );
};
